import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import BrowserEmbedError from "src/components/embedTypes/BrowserEmbedError";
import ContentError from "src/components/embedTypes/ContentError";
import ContentLoading from "src/components/embedTypes/ContentLoading";
import ContentTypesRouter from "src/components/embedTypes/ContentTypesRouter";
import { SiteContext } from "src/context/site";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { classNames } from "src/helpers/classNames";
import { getNavigateLink } from "src/helpers/userHelper";
import useBrowserType from "src/hooks/useBrowserType";
import useCheckThirdPartyCookies from "src/hooks/useCheckThirdPartyCookies";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import Maintenance from "src/components/shared/Maintenance";
import DocumentEmbedModal from "./DocumentEmbedModal";
import DocumentRadialMenu from "src/components/shared/DocumentRadialMenu";
import { AuthContext } from "src/context/auth";
import RefreshTimer from "./RefreshTimer";

const Dashboard = ({ isHeaderMenu = false }) => {
  const { loading, details: workspaceDetails, menuType, setWorspacePaymentModalOpen = () => {}, params } = useContext(WorkspaceContext);
  const { site } = useContext(SiteContext);
  const { user } = useContext(AuthContext);

  const { workspace_id, content_type, content_value } = useParams();
  const { group, page, form, document } = useUrlQueryParams();
  const { finishSwitching } = useWorkspaceSwitching();
  const { thirdPartyCookiesEnabled, cookieCheckComplete } = useCheckThirdPartyCookies();
  const navigate = useNavigate();
  const browser = useBrowserType();

  const [loadingPage, setLoadingPage] = useState(true);
  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [browserError, setBrowserError] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [altLoader, setAltLoader] = useState(false);

  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [activeDocumentTab, setActiveDocumentTab] = useState("view");

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [loadingUploadedDocuments, setLoadingUploadedDocuments] = useState(false);

  const pageErrorTypes = {
    page_error: {
      title: "Page Error",
      subtitle: "Oops... Something went wrong while loading the page.",
      secondary: "Please try refreshing the page or come back later.",
    },
    payment_error: {
      title: "Payment Issue",
      subtitle: "We're having trouble with the payment system.",
      secondary: "Please try again later or contact support if the issue persists.",
    },
    incorrect_credentials: {
      title: "Login Error",
      subtitle: "Oops... The credentials entered are incorrect.",
      secondary: "Please double-check your username and password, and try again.",
    },
    incorrect_filters: {
      title: "Filter Error",
      subtitle: "Oops... The filters applied are invalid or incomplete.",
      secondary: "Please verify the filters and try again.",
    },
  };

  const handlePageGet = async (signal, isBackground = false) => {
    try {
      if (!isHeaderMenu) {
        localStorage.setItem("workspace", workspace_id || "");
        localStorage.setItem("group", group || "");
        localStorage.setItem("page", page || "");
      }

      if (!isBackground) {
        setLoadingPage(true);
        setContent(null);
      } else {
        setIsRefreshing(true);
      }

      setContentError(null);
      setMaintenanceMode(false);

      let queries = {
        group,
        page,
        form,
        document,
        ...(content_type && content_value ? { [content_type]: content_value } : {}),
      };

      if (isBackground) {
        queries.request_type = "auto_refresh";
      }

      const { data: contentResponseData } = await apiRequest(
        "GET",
        isHeaderMenu ? "/portal/header-menu/embed/:workspace_id" : `/portal/embed/:workspace_id`,
        {
          params: { workspace_id },
          queries,
        },
        { returnErrorWithstatus: true, signal, is_abort: true },
      );

      const { data } = contentResponseData;

      if (data?.config?.type !== "IFRAME_EMBED") finishSwitching();

      setIsRefreshing(false);

      if (data?.invalidWorkspace) {
        navigate("/permission-denied");
      } else if (data?.invalidPage || data?.invalidGroup) {
        const { data: resData } = await apiRequest("GET", `/portal/users/${workspace_id}/page`, { queries: { group } });
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else if (data?.invalidFilter) {
        setContentError("incorrect_filters");
      } else {
        setContent(data);
        if (data?.config?.type !== "IFRAME_EMBED") setLoadingPage(false);
      }
    } catch (error) {
      setIsRefreshing(false);
      if (error.message !== "canceled") {
        setLoadingPage(false);
        finishSwitching();
        if (error?.status === 423) {
          setMaintenanceMode(true);
          return;
        }
        if (error?.response?.data?.data?.invalidFilter) {
          setContentError("incorrect_filters");
        } else {
          if (error?.status === 402) {
            setContentError("payment_error");
            navigate(`/workspace/${workspace_id}/payment-required`);
          } else {
            setContentError("page_error");
          }
        }
      }
    }
  };

  const handleRetryAfterMaintenance = () => {
    setMaintenanceMode(false);
    handlePageGet();
  };

  const getUploadedDocuments = async () => {
    if (!user?._id) return;
    try {
      const queries = {
        group,
        page,
        form,
        document,
        keyword: "",
        ...(content_type && content_value ? { [content_type]: content_value } : {}),
      };
      setUploadedDocuments([]);
      setLoadingUploadedDocuments(true);
      const { data } = await apiRequest("get", "portal/workspaces/:workspace_id/documents", { params: { workspace_id }, queries });
      setUploadedDocuments(data?.data);
    } catch (error) {
    } finally {
      setLoadingUploadedDocuments(false);
    }
  };

  useEffect(() => {
    getUploadedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, page, form, document, workspace_id, isHeaderMenu, content_type, content_value]);

  useEffect(() => {
    const controller = new AbortController();

    if (workspace_id) {
      setWorspacePaymentModalOpen(false);
      handlePageGet(controller.signal);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, page, form, document, workspace_id, isHeaderMenu, content_type, content_value]);

  useEffect(() => {
    if (cookieCheckComplete) {
      if (!browser) return;
      if (content?.config?.type !== "JWT_FULL_EMBED") return;

      const shouldShowBrowserError = () => {
        if (!thirdPartyCookiesEnabled) return "3PartyCookiesNotEnabled";
        if (!browser.includes("Chrome", "Edge")) return "NotUsingChromeOrEdge";
        return false;
      };

      let errorMessage = shouldShowBrowserError();
      setBrowserError(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieCheckComplete, browser, thirdPartyCookiesEnabled]);

  useEffect(() => {
    const ports = {};
    const handleMessage = (event) => {
      if (!event.ports[0]) return;
      const referenceId = event.data.referenceId;
      const port = event.ports[0];
      port.start();
      const onPortMessage = () => {};

      port.onmessage = onPortMessage;
      ports[referenceId] = port;
    };

    const onIFrameLoaded = (e) => {
      finishSwitching();
      setLoadingPage(false);
    };

    const onTokenExpired = () => {
      handlePageGet();
    };

    window.addEventListener("message", handleMessage, { passive: true });
    window.addEventListener("domo:iframe-loaded", onIFrameLoaded, { passive: true });
    window.addEventListener("metabase:token-expired", onTokenExpired, { passive: true });

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("domo:iframe-loaded", onIFrameLoaded);
      window.removeEventListener("metabase:token-expired", onTokenExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishSwitching, loading]);

  useEffect(() => {
    setAltLoader(params?.page_type === "METABASE" ? true : false);
  }, [params?.page_type]);

  return (
    <>
      <div className={classNames("relative flex h-full w-full", loadingPage && "h-[calc(100vh-4rem)] overflow-hidden")}>
        {contentError || browserError ? (
          browserError ? (
            <BrowserEmbedError
              browser={browser}
              cookiesEnabled={thirdPartyCookiesEnabled}
            />
          ) : (
            <ContentError
              pageErrorTypes={pageErrorTypes}
              pageError={contentError}
              refresh={() => handlePageGet()}
            />
          )
        ) : maintenanceMode ? (
          <Maintenance onRetry={handleRetryAfterMaintenance} />
        ) : (
          content?.config?.provider && (
            <div className="relative flex w-full flex-grow flex-col">
              <ContentTypesRouter
                content={content}
                menuType={menuType}
                workspace={workspaceDetails}
                site={site}
                setError={() => {}}
                setLoading={() => {}}
              />
            </div>
          )
        )}

        {!loadingPage && !loadingUploadedDocuments && (
          <DocumentRadialMenu
            content={content}
            documentModalOpen={documentModalOpen}
            setDocumentModalOpen={setDocumentModalOpen}
            setActiveDocumentTab={setActiveDocumentTab}
            page_type={params?.page_type}
            uploadedDocuments={uploadedDocuments}
          />
        )}

        {loadingPage && (
          <ContentLoading
            menuType={menuType}
            overlay={true}
            altLoader={altLoader}
          />
        )}

        <RefreshTimer
          enabled={content?.data?.auto_refresh_enabled}
          timeframe={content?.data?.auto_refresh_timeframe}
          onRefresh={handlePageGet}
          isRefreshing={isRefreshing}
        />
      </div>
      <DocumentEmbedModal
        content={content}
        documentModalOpen={documentModalOpen}
        setDocumentModalOpen={setDocumentModalOpen}
        activeTab={activeDocumentTab}
        setActiveTab={setActiveDocumentTab}
      />
    </>
  );
};

export default Dashboard;
